import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useDocumentBlob } from 'domain/shared/documentStorage';
import { ShowSection } from 'framework/ui/layout';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import PublishProcessAsset from '../shared/buttons/PublishProcessAsset';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import ViewModeToggle from './ViewModeToggle';
import DocumentPreviewBlock from './DocumentPreviewBlock';
import usePublishAssetData, { PUBLISH_ASSET_MODES, ViewMode } from './usePublishAssetData';
import { DiffViewer, MAX_PERCENT_CHANGES } from 'framework/DiffViewer';

const PublishProcessAssetContent = () => {
  const {
    documentPath,
    previewRenderPath,
    sourceDiff,
    renderedDiff,
    uploadedBy,
    isFirstPublish,
    percentChanges,

    canShowRendered,
    canToggleView,
    canShowSourceDiff,
    canShowRenderedDiff,
  } = usePublishAssetData();

  const [viewMode, setViewMode] = useState<ViewMode>(PUBLISH_ASSET_MODES.RENDERED);
  const [isExpanded, setIsExpanded] = useState(false);

  const sourceBlob = useDocumentBlob(documentPath);
  const renderedBlob = useDocumentBlob(previewRenderPath);

  // ---- protect invalid state ----
  useEffect(() => {
    if (viewMode === PUBLISH_ASSET_MODES.RENDERED && !canShowRendered) {
      setViewMode(PUBLISH_ASSET_MODES.SOURCE);
    }
  }, [canShowRendered, viewMode]);

  // ---- expand logic ----
  useEffect(() => {
    if (
      isFirstPublish ||
      percentChanges === 0 ||
      percentChanges > MAX_PERCENT_CHANGES ||
      (viewMode === PUBLISH_ASSET_MODES.SOURCE && !canShowSourceDiff) ||
      (viewMode === PUBLISH_ASSET_MODES.RENDERED && !canShowRenderedDiff)
    ) {
      setIsExpanded(true);
    }
  }, [canShowRenderedDiff, canShowSourceDiff, isFirstPublish, percentChanges, viewMode]);

  // ---- active mode ----
  const isRenderedActive = viewMode === PUBLISH_ASSET_MODES.RENDERED && canShowRendered;

  const activeBlob = isRenderedActive ? renderedBlob : sourceBlob;
  const activePath = isRenderedActive ? previewRenderPath : documentPath;

  const activeDiff = isRenderedActive
    ? canShowRenderedDiff
      ? renderedDiff
      : null
    : canShowSourceDiff
      ? sourceDiff
      : null;

  const canShowDiffSection =
    !!activeDiff && !isFirstPublish && percentChanges > 0 && percentChanges <= MAX_PERCENT_CHANGES;

  return (
    <ShowSection sx={{ m: '0 !important', pb: '15px !important' }}>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Box width="80%">
          {canToggleView && (
            <Box display="flex" justifyContent="center" mb={2}>
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
            </Box>
          )}

          <Typography variant="body1" color="textSecondary" align="center" mb={2} fontStyle="italic">
            {isRenderedActive ? 'Viewing rendered template' : 'Viewing source template'}
          </Typography>

          <DocumentPreviewBlock blob={activeBlob} path={activePath} isExpanded={isExpanded} />

          {canShowDiffSection && (
            <>
              <Box textAlign="center" mt={1}>
                <Button onClick={() => setIsExpanded(v => !v)} variant="text" size="small">
                  <KeyboardDoubleArrowDownIcon
                    sx={{
                      mr: 1,
                      fontSize: '14px',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                  {isExpanded ? 'Hide' : 'Show full template'}
                  <KeyboardDoubleArrowDownIcon
                    sx={{
                      ml: 1,
                      fontSize: '14px',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </Button>
              </Box>

              <DiffViewer difference={activeDiff!.differences} />
            </>
          )}

          <Box display="flex" justifyContent="space-between">
            <Typography
              sx={{
                '.field__wrapper': {
                  display: 'inline-flex',
                  ml: '4px',
                },
              }}
            >
              If you have any questions regarding the template updates, please contact the template loader
              {uploadedBy && <EmployeeLinkFieldWithNameLoading source="metadata.uploadedBy" label="" />}
            </Typography>
          </Box>

          <PublishProcessAsset />
        </Box>
      </Box>
    </ShowSection>
  );
};

export default PublishProcessAssetContent;
